import { useCallback, useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { useConfirm } from "@omit/react-confirm-dialog";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { Contact } from "@/bryntum-lib/models/Contact.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import { ContactFormData, ILinkedEntity } from "@/react/schema/contactSchema";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils.jsx";
import { EntityRecord } from "@/types/entity";
import { TaskContact } from "@/bryntum-lib/models/TaskContact";
import { ResourceContact } from "@/bryntum-lib/models/ResourceContact";
import { ClientContact } from "@/bryntum-lib/models/ClientContact";
import { UserContact } from "@/bryntum-lib/models/UserContact";
import { TagValue } from "@/bryntum-lib/models/TagValue";
import { defaultCellRenderer } from "@/other/utils/utils.jsx";

// Export types for use in context
export type TEntityContact = TaskContact | ResourceContact | ClientContact | UserContact;

export interface TagStore {
  id: number;
  tagValue: string;
  tagSet: string;
}

interface IUseSwivuContactProps {
  entityRecord: EntityRecord;
}

export interface TUseSwivuContactReturn {
  // Contact operations
  contacts: Contact[];
  loading: boolean;
  canEdit: boolean;
  createContact: (data: ContactFormData, linkedEntities: ILinkedEntity[]) => Promise<void>;
  updateContact: (contactId: string | number, data: ContactFormData, linkedEntitiesChanges: { added: ILinkedEntity[]; removed: ILinkedEntity[] }) => Promise<void>;
  deleteContact: (contactId: string | number) => Promise<boolean>;
  assignContacts: (contactIds: (string | number)[]) => Promise<void>;
  unassignContact: (contactId: string | number) => Promise<void>;
  copyContactToClipboard: (contact: Contact) => void;
  refreshContacts: () => void;
  getTagStores: () => {
    statusStore: TagStore[];
    categoryStore: TagStore[];
    tagsStore: TagStore[];
  };
  getAvailableContacts: () => Contact[];
}

export const useSwivuContact = ({ entityRecord }: IUseSwivuContactProps): TUseSwivuContactReturn => {
  const { project, user } = useSwivuBryntumContext();
  const { contactsStore } = project;
  const [loading, setLoading] = useState<boolean>(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const confirm = useConfirm();

  // Determine entity type and store keys - memoized since it doesn't change
  const entityInfo = useMemo(() => {
    if (entityRecord.isTask || entityRecord.isPlanTaskModel) {
      return {
        entityType: "taskId",
        entityStoreKey: STORE_KEYS.taskStore,
        entityContactStoreKey: STORE_KEYS.taskContactStore
      };
    } else if (entityRecord.isResource) {
      return {
        entityType: "resourceId",
        entityStoreKey: STORE_KEYS.resourceStore,
        entityContactStoreKey: STORE_KEYS.resourceContactStore
      };
    } else if (entityRecord.isClient) {
      return {
        entityType: "clientId",
        entityStoreKey: STORE_KEYS.clientStore,
        entityContactStoreKey: STORE_KEYS.clientContactStore
      };
    } else {
      // Assume User
      return {
        entityType: "userId",
        entityStoreKey: STORE_KEYS.boardUserStore,
        entityContactStoreKey: STORE_KEYS.userContactStore
      };
    }
  }, [entityRecord]);

  // RBAC check
  const canEditContact = useCallback((): boolean => {
    return user.can(GRANTS.grant_four);
  }, [user]);

  // Refresh contacts from entity
  const refreshContacts = useCallback(() => {
    const selectedBoard = getSavedBoardSelection();
    const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);

    // Get entity contacts (e.g., task.contacts returns TaskContact[])
    const entityContacts = entityRecord.contacts || ([] as unknown as TEntityContact[]);

    // Filter and sort contacts
    const filteredContacts = entityContacts
      .filter((entityContact: TEntityContact) => {
        const contactRecord = entityContact.contactRecord;
        return contactRecord && (canShowArchivedRecords || !contactRecord.archived);
      })
      .sort((a: TEntityContact, b: TEntityContact) => (a.order || 0) - (b.order || 0))
      .map((entityContact: TEntityContact) => entityContact.contactRecord);

    setContacts(filteredContacts);
  }, [entityRecord]);

  // Create new contact with entity association
  const createContact = useCallback(
    async (data: ContactFormData, linkedEntities: ILinkedEntity[]): Promise<void> => {
      try {
        setLoading(true);
        project.beginBatch();

        // Create contact record
        const contactRecord = contactsStore.createRecord({
          name: data.name,
          phone: data.phone || "",
          email: data.email || "",
          address: data.address || "",
          statusId: data.statusId,
          categoryId: data.categoryId,
          tagsId: data.tagsId || [],
          noteText: data.noteText || ""
        }) as unknown as Contact;

        contactsStore.add(contactRecord);

        // Wait for ID assignment
        await new Promise((resolve) => {
          contactsStore.on({
            async idChange({ record }: { record: Contact }) {
              if (record.id === contactRecord.id) {
                resolve(undefined);
              }
            },
            once: true
          });
        });

        // Link to current entity
        const { entityType, entityContactStoreKey } = entityInfo;
        const entityContactStore = project.getCrudStore(entityContactStoreKey);
        entityContactStore.add({
          contactId: contactRecord.id,
          [entityType]: entityRecord.id
        });

        // Link to other entities
        linkedEntities.forEach((linkedEntity) => {
          const store = project.getCrudStore(linkedEntity.entityContactStoreKey);
          if (!store) {
            console.warn(`Store not found for key: ${linkedEntity.entityContactStoreKey}`);
            return;
          }
          if (!linkedEntity.entityRecord?.id) {
            console.warn(`Invalid entity record:`, linkedEntity);
            return;
          }
          store.add({
            contactId: contactRecord.id,
            [linkedEntity.entityRelationKey]: linkedEntity.entityRecord.id
          });
        });

        project.endBatch();

        toast.success("Contact created successfully");
        refreshContacts();
      } catch (error) {
        project.endBatch();
        console.error("Failed to create contact:", error);
        toast.error("Failed to create contact");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [contactsStore, entityRecord, entityInfo, project, refreshContacts]
  );

  // Update existing contact
  const updateContact = useCallback(
    async (contactId: string | number, data: ContactFormData, linkedEntitiesChanges: { added: ILinkedEntity[]; removed: ILinkedEntity[] }): Promise<void> => {
      try {
        setLoading(true);
        const contactRecord = contactsStore.getById(contactId) as unknown as Contact;

        if (!contactRecord) {
          throw new Error("Contact not found");
        }

        project.beginBatch();

        // Update contact fields
        contactRecord.set({
          name: data.name,
          phone: data.phone || "",
          email: data.email || "",
          address: data.address || "",
          statusId: data.statusId,
          categoryId: data.categoryId,
          tagsId: data.tagsId || [],
          noteText: data.noteText || ""
        });

        // Remove unlinked entities
        linkedEntitiesChanges.removed.forEach((linkedEntity) => {
          const store = project.getCrudStore(linkedEntity.entityContactStoreKey);
          if (!store) {
            console.warn(`Store not found for key: ${linkedEntity.entityContactStoreKey}`);
            return;
          }
          const entityContact = store.query((ec: TEntityContact) => ec.contactId === contactId && ec[linkedEntity.entityRelationKey] === linkedEntity.entityRecord.id, true)[0];

          if (entityContact) {
            store.remove(entityContact);
          }
        });

        // Add newly linked entities
        linkedEntitiesChanges.added.forEach((linkedEntity) => {
          const store = project.getCrudStore(linkedEntity.entityContactStoreKey);
          if (!store) {
            console.warn(`Store not found for key: ${linkedEntity.entityContactStoreKey}`);
            return;
          }
          if (!linkedEntity.entityRecord?.id) {
            console.warn(`Invalid entity record:`, linkedEntity);
            return;
          }
          store.add({
            contactId: contactId,
            [linkedEntity.entityRelationKey]: linkedEntity.entityRecord.id
          });
        });

        project.endBatch();

        toast.success("Contact updated successfully");
        refreshContacts();
      } catch (error) {
        project.endBatch();
        console.error("Failed to update contact:", error);
        toast.error("Failed to update contact");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [contactsStore, project, refreshContacts]
  );

  // Delete contact
  const deleteContact = useCallback(
    async (contactId: string | number): Promise<boolean> => {
      const contactRecord = contactsStore.getById(contactId) as unknown as Contact;
      if (!contactRecord) return false;

      const confirmed = await confirm({
        title: "Confirm Delete",
        description: `Are you sure you want to delete "${contactRecord.name}"? This action cannot be undone.`
      });

      if (!confirmed) return false;

      try {
        setLoading(true);
        contactsStore.remove(contactId);
        toast.success("Contact deleted successfully");
        refreshContacts();
        return true;
      } catch (error) {
        console.error("Failed to delete contact:", error);
        toast.error("Failed to delete contact");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [contactsStore, confirm, refreshContacts]
  );

  // Assign existing contacts to entity
  const assignContacts = useCallback(
    async (contactIds: (string | number)[]): Promise<void> => {
      try {
        setLoading(true);
        const { entityType, entityContactStoreKey } = entityInfo;
        const entityContactStore = project.getCrudStore(entityContactStoreKey);

        project.beginBatch();

        contactIds.forEach((contactId) => {
          entityContactStore.add({
            contactId: contactId,
            [entityType]: entityRecord.id
          });
        });

        project.endBatch();

        toast.success(`${contactIds.length} contact${contactIds.length > 1 ? "s" : ""} assigned successfully`);
        refreshContacts();
      } catch (error) {
        project.endBatch();
        console.error("Failed to assign contacts:", error);
        toast.error("Failed to assign contacts");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [entityRecord, entityInfo, project, refreshContacts]
  );

  // Unassign contact from entity
  const unassignContact = useCallback(
    async (contactId: string | number): Promise<void> => {
      try {
        // Check for self-contact protection
        if (entityRecord.selfContactId === contactId) {
          toast.error("Cannot remove user's self contact");
          return;
        }

        const confirmed = await confirm({
          title: "Confirm Unassign",
          description: "Are you sure you want to remove this contact from this entity?"
        });

        if (!confirmed) return;

        setLoading(true);
        const { entityType, entityContactStoreKey } = entityInfo;
        const entityContactStore = project.getCrudStore(entityContactStoreKey);

        // Find and remove the entity-contact join record
        const entityContact = entityContactStore.query((ec: TEntityContact) => ec.contactId === contactId && ec[entityType] === entityRecord.id, true)[0];

        if (entityContact) {
          entityContactStore.remove(entityContact);
          toast.success("Contact unassigned successfully");
          refreshContacts();
        }
      } catch (error) {
        console.error("Failed to unassign contact:", error);
        toast.error("Failed to unassign contact");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [entityRecord, entityInfo, project, confirm, refreshContacts]
  );

  // Get tag stores
  const getTagStores = useCallback(() => {
    const contactTagValueStore = project.getCrudStore(STORE_KEYS.contactTagValuesStore);

    return {
      statusStore: contactTagValueStore.query((s: TagValue) => s.tagSet === "Status", true) as unknown as TagStore[],
      categoryStore: contactTagValueStore.query((s: TagValue) => s.tagSet === "Category", true) as unknown as TagStore[],
      tagsStore: contactTagValueStore.query((s: TagValue) => s.tagSet === "Tag", true) as unknown as TagStore[]
    };
  }, [project]);

  // Get all available contacts for assignment (excluding already assigned)
  const getAvailableContacts = useCallback(() => {
    const allContacts = contactsStore.query(() => true, true) as unknown as Contact[];
    const assignedContactIds = contacts.map((c: Contact) => c.id);

    return allContacts.filter((contact: Contact) => !assignedContactIds.includes(contact.id));
  }, [contactsStore, contacts]);

  // Copy contact to clipboard
  const copyContactToClipboard = useCallback((contact: Contact) => {
    const contactName = defaultCellRenderer({ value: contact.name });
    const contactPhone = defaultCellRenderer({ value: contact.phone });
    const contactEmail = defaultCellRenderer({ value: contact.email });
    const contactAddress = defaultCellRenderer({ value: contact.address });
    const contactText = `${contactName}, ${contactPhone}, ${contactEmail}, ${contactAddress}`;
    navigator.clipboard.writeText(contactText);
    toast.success("Contact has been copied to clipboard.");
  }, []);

  // Initialize contacts
  useEffect(() => {
    refreshContacts();
  }, [refreshContacts]);

  return {
    contacts,
    loading,
    canEdit: canEditContact(),
    createContact,
    updateContact,
    deleteContact,
    assignContacts,
    unassignContact,
    copyContactToClipboard,
    refreshContacts,
    getTagStores,
    getAvailableContacts
  };
};
